/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// redux utils
import { createSlice } from '@reduxjs/toolkit';
// thunks
import { 
    fetchArticleSentimentV2Config, 
    fetchEmojisCategories, 
    updateArticleSentimentV2Config, 
    updateArticleSentimentV2DisplayConfig,

    fetchReactionClickRateReport,
    fetchReactionClicksReport,
    fetchPopularArticlesReport,
    fetchPopularArticlesExportAll,
    fetchInViewRateReport,
    fetchTotalEmbedLoadsReport
} from 'state/thunks/articlesentimentv2';
import { fetchProductDataLastUpdated } from 'state/thunks/product';
import { ANALYTICS_DETAILS_DEFAULT_DATA } from 'utils/consts';


/* ========== ~~~~~~~~~~ ARTICLE SENTIMENT V2 CONFIG SLICE : REDUCER ~~~~~~~~~~ ========== */

const initialDisplayConfig = {
    customBackgroundColor: "#FFFFFF",
    customPrimaryColor: "#061240",
    enableMonochrome: false,
    useSystemFont: false,
    fontTypeface: "DECORATIVE_AND_PLAIN",
    fontSize: "SMALL",
    emojiConfigs: []
}
const initialState = {
    lastUpdated: null,
    reactionClickRateReport: {
        overview: null,
        details: ANALYTICS_DETAILS_DEFAULT_DATA,
        status: 'initial',
        isDataReceived: false
    },
    reactionClicksReport: {
        overview: null,
        details: ANALYTICS_DETAILS_DEFAULT_DATA,
        status: 'initial',
        isDataReceived: false
    },
    popularArticlesReport: {
        data: [],
        status: 'initial',
        isDataReceived: false
    },
    inViewRateReport: {
        overview: null,
        details: ANALYTICS_DETAILS_DEFAULT_DATA,
        status: 'initial',
        isDataReceived: false
    },
    totalEmbedLoadsReport: {
        overview: null,
        details: ANALYTICS_DETAILS_DEFAULT_DATA,
        status: 'initial',
        isDataReceived: false
    },
    isConfigValid: {
        generalConfig: true,
        advancedConfig: true,
        adsConfig: true
    },
    tempArticleSentimentConfig: null,
    tempDisplayConfig: initialDisplayConfig,
    isArticleSentimentConfigUpdating: false,
    articleSentimentConfig: {
        articleSentimentUnitId: null,
        status: 'initial',
        generalConfig: {
            enableHeader: true,
            headline: 'What do you think?',
            enableHeaderBorder: true,
            headerIcon: 'SMILE_BLACK', // SMILE_BLACK, SMILE_WHITE, THOUGHT_BALLOON_BLACK, THOUGHT_BALLOON_WHITE, THINKING_FACE_WHITE, NONE

            contentAlignment: 'CENTERED',
            enableCombinedReactionCount: false,
            enableIndividualReactionCount: false,
            enableTooltipHoverOnEmoji: true
        },
         adsConfig: {
            beforeDesktopUnit: false,
            afterDesktopUnit: false,
            beforeMobileUnit: false,
            afterMobileUnit: false,
            desktopVisibility: 'ALL_USERS',
            mobileVisibility: 'ALL_USERS'
        },
        displayConfig: initialDisplayConfig,
        emojiCategoryType: "THREE_D"
    },
};


export const articleSentimentV2Slice = createSlice({
    name: 'articleSentimentV2Config',
    initialState,
    reducers: {
        setTempArticleSentimentConfig: (state, action) => {
            const { configName, data } = action.payload
            state.tempArticleSentimentConfig[configName] = data
        },
        setTempDisplayConfig: (state, action) => {
            state.tempDisplayConfig.config = action.payload
        },
        setTempDisplayConfigEmojis: (state, action) => {
            state.tempDisplayConfig.emojis = action.payload
        },
        revertConfigs: (state, action) => {
            const { configName } = action.payload
            state.tempArticleSentimentConfig[configName] = state.articleSentimentConfig[configName]
        },
        setArticleSentimentConfigSectionValid: (state, action) => {
            const { configName, isValid } = action.payload
            state.isConfigValid[configName] = isValid
        }
    },
    extraReducers: builder => {
      builder
        // fetch article sentiment v2 config
        .addCase(fetchArticleSentimentV2Config.pending, (state, action) => {
            state.articleSentimentConfig = {...initialState.articleSentimentConfig, status: 'pending'}
            state.tempArticleSentimentConfig = state.articleSentimentConfig
        })
        .addCase(fetchArticleSentimentV2Config.fulfilled, (state, action) => {
            state.articleSentimentConfig = {...action.payload, status: 'fulfilled'}
            state.tempArticleSentimentConfig = state.articleSentimentConfig

            if(action.payload) {
                const { emojiConfigs } = action.payload
                let emojiCategoryType = 'THREE_D'
                if(emojiConfigs.length) 
                    emojiCategoryType = (emojiConfigs.length ? emojiConfigs.map(emoji => emoji.style)?.pop() : 'THREE_D')
                state.articleSentimentConfig.emojiCategoryType = emojiCategoryType

                state.tempArticleSentimentConfig = state.articleSentimentConfig
                state.tempArticleSentimentConfig.emojiCategoryType = emojiCategoryType
            }
        })
        .addCase(fetchArticleSentimentV2Config.rejected, (state, action) => {
            state.articleSentimentConfig = {...initialState.articleSentimentConfig, status: 'rejected'}
            state.tempArticleSentimentConfig = state.articleSentimentConfig
        })   

        // fetch emoji categories
        .addCase(fetchEmojisCategories.pending, (state, action) => {
            state.emojiCategories = []
        })
        .addCase(fetchEmojisCategories.fulfilled, (state, action) => {
            if(action.payload) state.emojiCategories = action.payload
        })
        
        // update display config config
        .addCase(updateArticleSentimentV2DisplayConfig.pending, (state, action) => {
            state.displayConfig.isUpdating = true
        })
        .addCase(updateArticleSentimentV2DisplayConfig.fulfilled, (state, action) => {
            if(action.payload) {
                const { emojis, config } = action.payload
                state.displayConfig.config = config
                state.displayConfig.emojis = emojis
            }
            state.displayConfig.isUpdating = false
            state.tempDisplayConfig = state.displayConfig
        })
        .addCase(updateArticleSentimentV2DisplayConfig.rejected, (state, action) => {
            state.displayConfig.isUpdating = false
        })    
        
        // update general config
        .addCase(updateArticleSentimentV2Config.pending, (state, action) => {
            state.articleSentimentConfig.isUpdating = true
        })
        .addCase(updateArticleSentimentV2Config.fulfilled, (state, action) => {
            if(action.payload) {
                state.articleSentimentConfig = action.payload
                state.tempArticleSentimentConfig = action.payload
            }
            state.articleSentimentConfig.isUpdating = false
        })
        .addCase(updateArticleSentimentV2Config.rejected, (state, action) => {
            state.articleSentimentConfig.isUpdating = false
        })    


        // fetch Reaction Click Rate Report
        .addCase(fetchReactionClickRateReport.pending, (state, action) => {
            state.reactionClickRateReport.status = 'pending'
            state.reactionClickRateReport.details = ANALYTICS_DETAILS_DEFAULT_DATA
        })
        .addCase(fetchReactionClickRateReport.fulfilled, (state, action) => {
            const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
            state.reactionClickRateReport.overview = Overview
            state.reactionClickRateReport.details = {daily: Daily, weekly: Weekly, monthly: Monthly, quarterly: Quarterly};
            state.reactionClickRateReport.status = 'fulfilled'
            state.reactionClickRateReport.isDataReceived = true
        })
        .addCase(fetchReactionClickRateReport.rejected, (state, action) => {
            state.reactionClickRateReport.status = 'rejected'
        })

        // fetch Reaction Clicks Report
        .addCase(fetchReactionClicksReport.pending, (state, action) => {
            state.reactionClicksReport.status = 'pending'
            state.reactionClicksReport.details = ANALYTICS_DETAILS_DEFAULT_DATA
        })
        .addCase(fetchReactionClicksReport.fulfilled, (state, action) => {
            const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
            state.reactionClicksReport.overview = Overview || state.reactionClickRateReport.overview
            if(Daily) state.reactionClicksReport.details.daily = Daily
            if(Weekly) state.reactionClicksReport.details.weekly = Weekly
            if(Monthly) state.reactionClicksReport.details.monthly = Monthly
            if(Quarterly) state.reactionClicksReport.details.quarterly = Quarterly
            state.reactionClicksReport.status = 'fulfilled'
            state.reactionClicksReport.isDataReceived = true
        })
        .addCase(fetchReactionClicksReport.rejected, (state, action) => {
            state.reactionClicksReport.status = 'rejected'
        })

        // fetch Popular Articles Report
        .addCase(fetchPopularArticlesReport.pending, (state, action) => {
            state.popularArticlesReport.status = 'pending'
        })
        .addCase(fetchPopularArticlesReport.fulfilled, (state, action) => {
            state.popularArticlesReport.data= action.payload
            state.popularArticlesReport.status = 'fulfilled'
            state.popularArticlesReport.isDataReceived = true
        })
        .addCase(fetchPopularArticlesReport.rejected, (state, action) => {
            state.popularArticlesReport.status = 'rejected'
        })
        

        // fetch In View Rate Report
        .addCase(fetchInViewRateReport.pending, (state, action) => {
            state.inViewRateReport.status = 'pending'
            state.inViewRateReport.details = ANALYTICS_DETAILS_DEFAULT_DATA
        })
        .addCase(fetchInViewRateReport.fulfilled, (state, action) => {
            const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
            state.inViewRateReport.overview = Overview || state.inViewRateReport.overview
            if(Daily) state.inViewRateReport.details.daily = Daily
            if(Weekly) state.inViewRateReport.details.weekly = Weekly
            if(Monthly) state.inViewRateReport.details.monthly = Monthly
            if(Quarterly) state.inViewRateReport.details.quarterly = Quarterly
            state.inViewRateReport.status = 'fulfilled'
            state.inViewRateReport.isDataReceived = true
        })
        .addCase(fetchInViewRateReport.rejected, (state, action) => {
            state.inViewRateReport.status = 'rejected'
        })

        // fetch Total Embed Loads Report
        .addCase(fetchTotalEmbedLoadsReport.pending, (state, action) => {
            state.totalEmbedLoadsReport.status = 'pending'
            state.totalEmbedLoadsReport.details = ANALYTICS_DETAILS_DEFAULT_DATA
        })
        .addCase(fetchTotalEmbedLoadsReport.fulfilled, (state, action) => {
            const { Overview, Daily, Weekly, Monthly, Quarterly } = action.payload
            state.totalEmbedLoadsReport.overview = Overview || state.totalEmbedLoadsReport.overview
            if(Daily) state.totalEmbedLoadsReport.details.daily = Daily
            if(Weekly) state.totalEmbedLoadsReport.details.weekly = Weekly
            if(Monthly) state.totalEmbedLoadsReport.details.monthly = Monthly
            if(Quarterly) state.totalEmbedLoadsReport.details.quarterly = Quarterly
            state.totalEmbedLoadsReport.status = 'fulfilled'
            state.totalEmbedLoadsReport.isDataReceived = true
        })
        .addCase(fetchTotalEmbedLoadsReport.rejected, (state, action) => {
            state.totalEmbedLoadsReport.status = 'rejected'
        })

        // fetch product data last updated timestamp
        .addCase(fetchProductDataLastUpdated.pending, (state, action) => {
            state.lastUpdated = null;
        })
        .addCase(fetchProductDataLastUpdated.fulfilled, (state, action) => {
            const { last_updated } = action.payload
            state.lastUpdated = last_updated;
        })
        .addCase(fetchProductDataLastUpdated.rejected, (state, action) => {
            state.lastUpdated = null;
        })
        
    }
});

export const { setTempArticleSentimentConfig, setTempDisplayConfig, setTempDisplayConfigEmojis, revertConfigs, setArticleSentimentConfigSectionValid } = articleSentimentV2Slice.actions
export default articleSentimentV2Slice.reducer;
